import { useEffect, useRef, useState, createContext, useContext } from "react";
import { exploreDivisionsEn, exploreDivisionsFr } from "./utlls/constants";
import BoxIcon from "assets/divisions/icons/box.svg";
import ArrowRightIcon from "assets/divisions/icons/arrow-right.svg";
import Button from "components/Button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ActiveDivisionContext = createContext(null);

export function ActiveDivisionProvider({ children }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Prevent unnecessary re-triggers
  const safeSetActiveIndex = (index) => {
    setActiveIndex((prev) => (prev === index ? prev : index));
  };

  return (
    <ActiveDivisionContext.Provider
      value={{ activeIndex, setActiveIndex: safeSetActiveIndex }}
    >
      {children}
    </ActiveDivisionContext.Provider>
  );
}

export function useActiveDivision() {
  const context = useContext(ActiveDivisionContext);

  if (!context) {
    throw new Error(
      "useActiveDivision must be used within an ActiveDivisionProvider",
    );
  }

  return context;
}

const ExploreDivision = () => {
  const { t } = useTranslation("home");

  return (
    <ActiveDivisionProvider>
      <div className="flex flex-col items-center w-full mb-10 gap-10">
        {/* Header */}
        <div className=" flex justify-center items-center md:w-[70%] xl:w-[60%]">
          <h1 className="text-[36px] md:text-[48px] font-medium text-center leading-[62px]">
            {t("exploreSection")}
          </h1>
        </div>

        <section className="md:h-[55vh] flex gap-16 items-start flex-col md:flex-row w-[90%]">
          <div className="md:w-1/2 max-h-[55vh] overflow-y-auto no-scrollbar">
            <ScrollableContent />
          </div>

          <div className="md:w-1/2 h-[55vh] sticky top-0 flex items-center justify-center">
            <DivisionImage />
          </div>
        </section>
      </div>
    </ActiveDivisionProvider>
  );
};

function ContentBlock({ division, index, setActiveIndex, rootRef }) {
  const ref = useRef(null);
  const language = localStorage.getItem("selectedLanguage");

  useEffect(() => {
    if (!rootRef?.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setActiveIndex(index);
        }
      },
      {
        root: rootRef.current, // 👈 important
        threshold: 0.6,
      },
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [index, setActiveIndex, rootRef]);

  return (
    <div ref={ref} className="min-h-[55vh] flex flex-col justify-center">
      <img src={division.logo} alt="logo" className="w-[100px] h-[23px] mb-3" />

      <h2 className="text-3xl font-semibold mb-2">{division.title}</h2>

      <p className="text-[#00000066] mb-6 md:w-[90%]">{division.description}</p>

      <div className="grid xl:grid-cols-2 gap-5 mb-10">
        {division.features.map((option, i) => (
          <div key={i} className="flex items-start gap-2">
            <img src={BoxIcon} alt="BoxIcon" className="mt-0.5" />
            <p className="text-[#00000066] text-base font-normal">
              <span className="font-semibold">{option.title}: </span>
              {option.description}
            </p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <Link to={division.link} target="_blank">
        <Button
          endIcon={
            <img
              src={ArrowRightIcon}
              alt="ArrowRightIcon"
              className="w-[16px] h-[8px]"
            />
          }
          variant="outlined"
          text={language?.includes("fr") ? "En savoir plus" : "Learn More"}
          size="sm"
          className="!px-10"
        />
      </Link>
    </div>
  );
}

function ScrollableContent() {
  const { setActiveIndex } = useActiveDivision();
  const containerRef = useRef(null);

  const language = localStorage.getItem("selectedLanguage");
  const exploreDivisions = language?.includes("fr")
    ? exploreDivisionsFr
    : exploreDivisionsEn;

  return (
    <div
      ref={containerRef}
      className="space-y-32 overflow-y-auto max-h-[55vh] no-scrollbar"
    >
      {exploreDivisions.map((division, index) => (
        <ContentBlock
          key={index}
          division={division}
          index={index}
          setActiveIndex={setActiveIndex}
          rootRef={containerRef}
        />
      ))}
    </div>
  );
}

function DivisionImage() {
  const { activeIndex } = useActiveDivision();
  const language = localStorage.getItem("selectedLanguage");

  const exploreDivisions = language?.includes("fr")
    ? exploreDivisionsFr
    : exploreDivisionsEn;

  const division = exploreDivisions[activeIndex];

  return (
    <div className="relative overflow-hidden">
      <img
        key={activeIndex}
        src={division.image}
        alt={division.title}
        className="
          w-[540px] h-[400px] object-contain
          animate-slideInRight
        "
      />
    </div>
  );
}

export default ExploreDivision;
